import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download, Mail, Trash2, Eye, Plus, Edit, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PopupDisplay from "@/components/PopupDisplay";
import { formatLocalDateTime } from "@/utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format dates consistently
const formatFlightDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  // If already in DD/MM/YYYY format, return as-is
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return dateStr;
  }
  
  // Handle YYYY-MM-DD format
  if (dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  
  return dateStr;
};

// Helper function to parse DD/MM/YYYY date strings
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return new Date(dateStr);
  }
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

// Helper function to format times
const formatFlightTime = (timeStr) => {
  if (!timeStr) return 'N/A';
  // If already in HH:MM format, return as-is
  if (timeStr.match(/^\d{2}:\d{2}$/)) {
    return timeStr;
  }
  // If in HH:MM:SS format, return HH:MM
  if (timeStr.includes(':')) {
    return timeStr.substring(0, 5);
  }
  return timeStr;
};

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [flights, setFlights] = useState([]); // Available flights for dropdown (without existing reports)
  const [allFlights, setAllFlights] = useState([]); // All flights for display purposes
  const [agencies, setAgencies] = useState([]); // List of agencies
  const [selectedAgencyId, setSelectedAgencyId] = useState(""); // Selected agency for new report
  const [agencyFlights, setAgencyFlights] = useState([]); // Flights filtered by selected agency
  const [flightDates, setFlightDates] = useState([]); // Array of dates that have flights
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedFlightId, setSelectedFlightId] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [formData, setFormData] = useState({
    flight_id: "",
    report_date: new Date().toISOString().split('T')[0],
    agent_name: "",
    group_name: "",
    tour_leader_name: "",
    tour_leader_phone: "",
    agency_emergency_contact: "",
    connection_info: "",
    duty_start_time: "",
    duty_end_time: "",
    actual_departure_time: "",
    gate_number: "",
    actual_passenger_count: "",
    no_show_list: [],
    go_show_list: [],
    service_completion_status: "completed",
    operational_notes: ""
  });
  const [availablePassengers, setAvailablePassengers] = useState([]);
  const [goShowInput, setGoShowInput] = useState("");
  // Initialize filterDate in DD/MM/YYYY format to match database format
  const formatTodayDDMMYYYY = () => {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  };
  const [filterDate, setFilterDate] = useState(formatTodayDDMMYYYY());
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [selectedDateFlights, setSelectedDateFlights] = useState([]); // Flights for the selected date
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isStaff = currentUser.role === 'staff';
  const isAgency = currentUser.role === 'agency';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [reports, selectedYear, selectedMonth]);

  useEffect(() => {
    // Filter flights by selected date in dialog
    if (filterDate && flights.length > 0) {
      const filtered = flights.filter(flight => flight.flight_date === filterDate);
      setFilteredFlights(filtered);
    } else {
      setFilteredFlights(flights);
    }
  }, [filterDate, flights]);

  const applyFilters = () => {
    let filtered = [...reports];

    // Filter by year
    if (selectedYear !== "all") {
      filtered = filtered.filter(report => {
        const reportYear = parseDateString(report.report_date)?.getFullYear()?.toString();
        return reportYear === selectedYear;
      });
    }

    // Filter by month
    if (selectedMonth !== "all") {
      filtered = filtered.filter(report => {
        const reportMonth = (parseDateString(report.report_date)?.getMonth() + 1)?.toString();
        return reportMonth === selectedMonth;
      });
    }

    // Sort by report_date (newest first)
    filtered.sort((a, b) => {
      const dateA = parseDateString(a.report_date);
      const dateB = parseDateString(b.report_date);
      return dateB - dateA; // Descending order (newest first)
    });

    setFilteredReports(filtered);
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [reportsRes, flightsRes, agenciesRes] = await Promise.all([
        axios.get(`${API}/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/flights`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agencies`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      let allReports = reportsRes.data;
      
      // Filter reports by agent_name for staff users
      if (isStaff) {
        allReports = allReports.filter(report => 
          report.agent_name === currentUser.full_name
        );
      }
      
      // Sort reports by report_date (newest first)
      allReports.sort((a, b) => {
        const dateA = parseDateString(a.report_date) || new Date(0);
        const dateB = parseDateString(b.report_date) || new Date(0);
        return dateB - dateA; // Descending order (newest first)
      });
      
      setReports(allReports);
      setAgencies(agenciesRes.data);
      
      // Store ALL flights for display purposes (to show flight info in existing reports)
      setAllFlights(flightsRes.data);
      
      // Filter out flights that already have reports (for the create dropdown)
      const reportedFlightIds = new Set(allReports.map(r => r.flight_id));
      const availableFlights = flightsRes.data.filter(f => !reportedFlightIds.has(f.id));
      setFlights(availableFlights);
      
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch data");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Convert YYYY-MM-DD to DD/MM/YYYY for backend
      const submitData = { ...formData };
      if (submitData.report_date && submitData.report_date.includes('-')) {
        const [year, month, day] = submitData.report_date.split('-');
        submitData.report_date = `${day}/${month}/${year}`;
      }
      
      await axios.post(`${API}/reports`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Report created successfully");
      setDialogOpen(false);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create report");
    }
  };

  const handleEdit = (report) => {
    setSelectedReport(report);
    const flight = allFlights.find(f => f.id === report.flight_id);
    
    // Get passenger names from "Passenger Names (one per line)" field
    let passengerList = [];
    if (flight && flight.passenger_names) {
      if (typeof flight.passenger_names === 'string') {
        passengerList = flight.passenger_names.split('\n').map(p => p.trim()).filter(p => p);
      } else if (Array.isArray(flight.passenger_names)) {
        passengerList = flight.passenger_names.filter(p => p && p.trim());
      }
    }
    setAvailablePassengers(passengerList);
    
    // Convert DD/MM/YYYY to YYYY-MM-DD for date input
    let reportDateForInput = '';
    if (report.report_date) {
      if (report.report_date.includes('/')) {
        const [day, month, year] = report.report_date.split('/');
        reportDateForInput = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else {
        reportDateForInput = report.report_date.substring(0, 10);
      }
    }
    
    setFormData({
      flight_id: report.flight_id,
      report_date: reportDateForInput,
      agent_name: report.agent_name || "",
      group_name: report.group_name || "",
      tour_leader_name: report.tour_leader_name || "",
      tour_leader_phone: report.tour_leader_phone || "",
      agency_emergency_contact: report.agency_emergency_contact || "",
      connection_info: report.connection_info || "",
      duty_start_time: report.duty_start_time || "",
      duty_end_time: report.duty_end_time || "",
      actual_departure_time: report.actual_departure_time || "",
      gate_number: report.gate_number || "",
      actual_passenger_count: report.actual_passenger_count || "",
      no_show_list: report.no_show_list || [],
      go_show_list: report.go_show_list || [],
      service_completion_status: report.service_completion_status || "completed",
      operational_notes: report.operational_notes || ""
    });
    setSelectedFlightId(report.flight_id);
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Convert YYYY-MM-DD back to DD/MM/YYYY for backend
      const submitData = { ...formData };
      if (submitData.report_date && submitData.report_date.includes('-')) {
        const [year, month, day] = submitData.report_date.split('-');
        submitData.report_date = `${day}/${month}/${year}`;
      }
      
      await axios.put(`${API}/reports/${selectedReport.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Report updated successfully");
      setEditDialogOpen(false);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update report");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Report deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const handleExportPDF = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF exported");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleSendEmail = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reports/${id}/send-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleAgencySelect = (agencyId) => {
    setSelectedAgencyId(agencyId);
    
    // Filter flights by selected agency
    const agencyFilteredFlights = flights.filter(f => f.agency_id === agencyId);
    setAgencyFlights(agencyFilteredFlights);
    
    // Extract unique dates from agency flights
    const dates = [...new Set(agencyFilteredFlights.map(f => f.flight_date))].sort();
    setFlightDates(dates);
    
    // Reset other selections
    setFilterDate("");
    setSelectedDateFlights([]);
    setSelectedFlightId("");
  };
  
  const handleDateSelect = (date) => {
    setFilterDate(date);
    
    // Get flights for the selected date
    const flightsOnDate = agencyFlights.filter(f => f.flight_date === date);
    setSelectedDateFlights(flightsOnDate);
    
    // If only one flight on this date, auto-select it
    if (flightsOnDate.length === 1) {
      handleFlightSelect(flightsOnDate[0].id);
    } else {
      // Reset flight selection if multiple flights
      setSelectedFlightId("");
    }
  };

  const handleFlightSelect = (flightId) => {
    setSelectedFlightId(flightId);
    const flight = agencyFlights.find(f => f.id === flightId) || flights.find(f => f.id === flightId);
    if (flight) {
      // Get passenger names from "Passenger Names (one per line)" field in flights
      let passengerList = [];
      if (flight.passenger_names) {
        if (typeof flight.passenger_names === 'string') {
          // If string, split by newline
          passengerList = flight.passenger_names.split('\n').map(p => p.trim()).filter(p => p);
        } else if (Array.isArray(flight.passenger_names)) {
          // If already array, use directly
          passengerList = flight.passenger_names.filter(p => p && p.trim());
        }
      }
      setAvailablePassengers(passengerList);
      
      // Calculate duty times: flight time -3h for start, flight time for end
      // ALL TIMES IN LOCAL - NO UTC CONVERSION
      let dutyStartTime = '';
      let dutyEndTime = '';
      let actualDepartureTime = '';
      let reportDate = '';
      
      if (flight.flight_date && flight.flight_time) {
        // Parse flight_date - handle both DD/MM/YYYY and YYYY-MM-DD formats
        let flightDateStr = flight.flight_date;
        
        // Check if date is in DD/MM/YYYY format
        if (flightDateStr.includes('/')) {
          const [day, month, year] = flightDateStr.split('/');
          flightDateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        // Set report date to same as flight date (in YYYY-MM-DD format for input)
        reportDate = flightDateStr;
        
        // Parse as local time string (no timezone conversion)
        const dateTimeStr = `${flightDateStr}T${flight.flight_time}`;
        
        // Create date object from local string
        const flightDateTime = new Date(dateTimeStr);
        
        // Duty start: 3 hours before flight time (LOCAL)
        const dutyStart = new Date(flightDateTime);
        dutyStart.setHours(dutyStart.getHours() - 3);
        
        // Format as local datetime-local string (YYYY-MM-DDTHH:MM)
        const pad = (n) => String(n).padStart(2, '0');
        dutyStartTime = `${dutyStart.getFullYear()}-${pad(dutyStart.getMonth() + 1)}-${pad(dutyStart.getDate())}T${pad(dutyStart.getHours())}:${pad(dutyStart.getMinutes())}`;
        
        // Duty end: flight time (LOCAL) - same as actual departure
        dutyEndTime = `${flightDateTime.getFullYear()}-${pad(flightDateTime.getMonth() + 1)}-${pad(flightDateTime.getDate())}T${pad(flightDateTime.getHours())}:${pad(flightDateTime.getMinutes())}`;
        
        // ACTUAL DEPARTURE: Use ATD from Planning (actual_time_departure) if available
        if (flight.atd) {
          // ATD is already stored as local datetime string
          actualDepartureTime = flight.atd;
        } else {
          // Default to flight scheduled time if ATD not set yet
          actualDepartureTime = dutyEndTime;
        }
      }
      
      setFormData({
        ...formData,
        flight_id: flightId,
        report_date: reportDate || new Date().toISOString().split('T')[0],
        agent_name: flight.agent_name || '',
        group_name: flight.group_name || '',
        tour_leader_name: flight.tour_leader_name || '',
        tour_leader_phone: flight.tour_leader_phone || '',
        agency_emergency_contact: flight.agency_emergency_contact || '',
        duty_start_time: dutyStartTime,
        duty_end_time: dutyEndTime,
        actual_departure_time: actualDepartureTime,
        actual_passenger_count: flight.passenger_count || '',
        gate_number: '',
      });
    }
  };

  const handleNoShowToggle = (passenger) => {
    setFormData(prev => ({
      ...prev,
      no_show_list: prev.no_show_list.includes(passenger)
        ? prev.no_show_list.filter(p => p !== passenger)
        : [...prev.no_show_list, passenger]
    }));
  };

  const handleAddGoShow = () => {
    if (goShowInput.trim()) {
      setFormData(prev => ({
        ...prev,
        go_show_list: [...prev.go_show_list, goShowInput.trim()]
      }));
      setGoShowInput("");
    }
  };

  const handleRemoveGoShow = (passenger) => {
    setFormData(prev => ({
      ...prev,
      go_show_list: prev.go_show_list.filter(p => p !== passenger)
    }));
  };

  const calculateDutyDuration = (start, end) => {
    if (!start || !end) return "N/A";
    try {
      // Handle HH:MM format
      const startParts = start.split(':');
      const endParts = end.split(':');
      if (startParts.length >= 2 && endParts.length >= 2) {
        const startMins = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        const endMins = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        const diffMins = endMins - startMins;
        if (diffMins >= 0) {
          const hours = Math.floor(diffMins / 60);
          const mins = diffMins % 60;
          return `${hours}h ${mins}m`;
        }
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  const resetForm = () => {
    setFormData({
      flight_id: "",
      report_date: new Date().toISOString().split('T')[0],
      agent_name: "",
      group_name: "",
      tour_leader_name: "",
      tour_leader_phone: "",
      agency_emergency_contact: "",
      connection_info: "",
      duty_start_time: "",
      duty_end_time: "",
      actual_departure_time: "",
      gate_number: "",
      actual_passenger_count: "",
      no_show_list: [],
      go_show_list: [],
      service_completion_status: "completed",
      operational_notes: ""
    });
    setSelectedFlightId("");
    setAvailablePassengers([]);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setViewDialogOpen(true);
  };

  // Calendar helper functions
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  // Format date as DD/MM/YYYY (European format - matching database storage)
  const formatDateToDDMMYYYY = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${d}/${m}/${year}`;
  };

  const isDateWithFlights = (year, month, day) => {
    const dateStr = formatDateToDDMMYYYY(year, month, day);
    return flightDates.includes(dateStr);
  };

  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const handleDateClick = (year, month, day) => {
    const dateStr = formatDateToDDMMYYYY(year, month, day);
    if (isDateWithFlights(year, month, day)) {
      handleDateSelect(dateStr);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  if (loading) return <div style={{ color: '#B0B0B0' }}>Loading...</div>;

  return (
    <div>
      <PopupDisplay location="reports" />
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Operational Reports</h1>
          <p style={{ color: '#B0B0B0' }}>Track actual flight operations and service delivery</p>
        </div>
        {!isStaff && !isAgency && (
          <Button onClick={() => setDialogOpen(true)} style={{ background: '#4A5A8A', color: '#FFFFFF' }}>
            <Plus className="mr-2 h-4 w-4" />Create Report
          </Button>
        )}
      </div>

      {/* Filters - Sticky */}
      <div className="sticky top-0 z-10 mb-6 p-4 rounded-lg" style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: '#4A5A8A', border: '1px solid' }}>
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <Label style={{ color: '#E8E8E8' }}>Year</Label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                <SelectItem value="all">All Years</SelectItem>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label style={{ color: '#E8E8E8' }}>Month</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                <SelectItem value="all">All Months</SelectItem>
                <SelectItem value="1">January</SelectItem>
                <SelectItem value="2">February</SelectItem>
                <SelectItem value="3">March</SelectItem>
                <SelectItem value="4">April</SelectItem>
                <SelectItem value="5">May</SelectItem>
                <SelectItem value="6">June</SelectItem>
                <SelectItem value="7">July</SelectItem>
                <SelectItem value="8">August</SelectItem>
                <SelectItem value="9">September</SelectItem>
                <SelectItem value="10">October</SelectItem>
                <SelectItem value="11">November</SelectItem>
                <SelectItem value="12">December</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 flex items-end">
            <p className="text-sm" style={{ color: '#B0B0B0' }}>
              Showing {filteredReports.length} of {reports.length} reports
            </p>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {filteredReports.length === 0 ? (
          <Card style={{ borderColor: '#4A5A8A', background: '#1E2A4A' }}>
            <CardContent className="p-8 text-center">
              <FileText className="mx-auto h-12 w-12 mb-4" style={{ color: '#B0B0B0' }} />
              <p style={{ color: '#B0B0B0' }}>No reports found</p>
            </CardContent>
          </Card>
        ) : (
          filteredReports.map((report) => {
            const flight = allFlights.find(f => f.id === report.flight_id);
            return (
              <Card key={report.id} style={{ borderColor: '#4A5A8A', background: '#1E2A4A' }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8" style={{ color: '#60A5FA' }} />
                      <div>
                        <h3 className="text-lg font-bold" style={{ color: '#FFFFFF' }}>
                          {flight ? flight.flight_number : 'Unknown Flight'}
                        </h3>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>
                          Date: {formatFlightDate(report.report_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!isStaff && !isAgency && (
                        <Button variant="outline" size="sm" onClick={() => handleEdit(report)} style={{ borderColor: '#60A5FA', color: '#60A5FA' }}>
                          <Edit className="mr-2 h-4 w-4" />Edit
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleViewReport(report)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                        <Eye className="mr-2 h-4 w-4" />View
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExportPDF(report.id)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                        <Download className="mr-2 h-4 w-4" />PDF
                      </Button>
                      {!isStaff && !isAgency && (
                        <Button variant="outline" size="sm" onClick={() => handleSendEmail(report.id)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                          <Mail className="mr-2 h-4 w-4" />Email
                        </Button>
                      )}
                      {!isStaff && !isAgency && (
                        <Button variant="outline" size="sm" onClick={() => handleDelete(report.id)} style={{ borderColor: '#EF4444', color: '#EF4444' }}>
                          <Trash2 className="mr-2 h-4 w-4" />Delete
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Agent</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{report.agent_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Gate</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{report.gate_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Actual PAX</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{report.actual_passenger_count || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Status</p>
                      <p className="font-medium capitalize" style={{ color: report.service_completion_status === 'completed' ? '#22C55E' : '#E8E8E8' }}>
                        {report.service_completion_status}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>No-Shows ({report.no_show_list?.length || 0}):</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>
                        {report.no_show_list?.length > 0 ? report.no_show_list.join(', ') : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Go-Shows ({report.go_show_list?.length || 0}):</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>
                        {report.go_show_list?.length > 0 ? report.go_show_list.join(', ') : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {report.connection_info && (
                    <div className="mb-4">
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Connection:</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{report.connection_info}</p>
                    </div>
                  )}

                  {report.operational_notes && (
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Notes:</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{report.operational_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Report Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          // Reset selections when dialog closes
          setSelectedAgencyId("");
          setAgencyFlights([]);
          setFlightDates([]);
          setFilterDate("");
          setSelectedDateFlights([]);
          setSelectedFlightId("");
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Create Operational Report</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>Fill in the flight operational details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Step 1: Select Agency */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>1. Select Agency *</Label>
              <Select value={selectedAgencyId} onValueChange={handleAgencySelect}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="Choose an agency" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  {agencies.filter(a => a.is_active !== false).map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAgencyId && (
                <p className="text-xs mt-1" style={{ color: '#60A5FA' }}>
                  ✓ Agency selected - {agencyFlights.length} flight(s) available
                </p>
              )}
            </div>

            {/* Step 2: Select Date - Calendar Widget */}
            {selectedAgencyId && (
              <div>
                <Label style={{ color: '#E8E8E8' }}>2. Select Flight Date *</Label>
                <p className="text-xs mb-2" style={{ color: '#B0B0B0' }}>
                  {flightDates.length > 0 ? `Agency has flights on ${flightDates.length} date(s). Click on bold dates to select.` : 'No flights available for this agency'}
                </p>
                
                {/* Calendar Widget */}
                <div style={{ 
                  background: '#0A1128', 
                  border: '1px solid #4A5A8A',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={handlePrevMonth}
                      className="p-1 rounded hover:bg-opacity-80"
                      style={{ background: '#1E2A4A', color: '#E8E8E8' }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <div style={{ color: '#E8E8E8', fontSize: '16px', fontWeight: '600' }}>
                      {monthNames[calendarMonth]} {calendarYear}
                    </div>
                    <button
                      type="button"
                      onClick={handleNextMonth}
                      className="p-1 rounded hover:bg-opacity-80"
                      style={{ background: '#1E2A4A', color: '#E8E8E8' }}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day Headers */}
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div 
                        key={day}
                        className="text-center text-xs py-1"
                        style={{ color: '#B0B0B0', fontWeight: '600' }}
                      >
                        {day}
                      </div>
                    ))}
                    
                    {/* Empty cells for days before month starts */}
                    {Array.from({ length: getFirstDayOfMonth(calendarMonth, calendarYear) }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    
                    {/* Calendar days */}
                    {Array.from({ length: getDaysInMonth(calendarMonth, calendarYear) }).map((_, i) => {
                      const day = i + 1;
                      const dateStr = formatDateToDDMMYYYY(calendarYear, calendarMonth, day);
                      const hasFlights = isDateWithFlights(calendarYear, calendarMonth, day);
                      const isSelected = filterDate === dateStr;
                      const today = new Date();
                      const isToday = today.getDate() === day && 
                                     today.getMonth() === calendarMonth && 
                                     today.getFullYear() === calendarYear;
                      
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDateClick(calendarYear, calendarMonth, day)}
                          disabled={!hasFlights}
                          className="text-center py-2 rounded text-sm"
                          style={{
                            background: isSelected ? '#4A5A8A' : (isToday ? '#1E2A4A' : 'transparent'),
                            color: hasFlights ? (isSelected ? '#FFFFFF' : '#E8E8E8') : '#4A5A8A',
                            fontWeight: hasFlights ? 'bold' : 'normal',
                            cursor: hasFlights ? 'pointer' : 'not-allowed',
                            border: isToday && !isSelected ? '1px solid #4A5A8A' : 'none',
                            opacity: hasFlights ? 1 : 0.4
                          }}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Selected date display */}
                  {filterDate && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #4A5A8A' }}>
                      <p className="text-xs" style={{ color: '#60A5FA' }}>
                        ✓ Selected: {formatFlightDate(filterDate)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Select Flight (if multiple on same date) */}
            {filterDate && selectedDateFlights.length > 0 && (
              <div>
                <Label style={{ color: '#E8E8E8' }}>3. Select Flight *</Label>
                <Select value={selectedFlightId} onValueChange={handleFlightSelect}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="Select a flight" />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    {selectedDateFlights.map((flight) => (
                      <SelectItem key={flight.id} value={flight.id}>
                        {flight.flight_number} - {formatFlightTime(flight.flight_time)} - {flight.departure_airport} → {flight.arrival_airport}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedFlightId && (
                  <p className="text-xs mt-1" style={{ color: '#60A5FA' }}>
                    ✓ Flight selected - All fields auto-filled (editable)
                  </p>
                )}
              </div>
            )}

            {/* Show form fields only after flight is selected */}
            {selectedFlightId && (
              <>
                {/* Separator */}
                <div style={{ 
                  borderTop: '2px solid #4A5A8A', 
                  marginTop: '20px', 
                  marginBottom: '20px',
                  paddingTop: '20px'
                }}>
                  <h3 style={{ color: '#4ADE80', fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                    Flight Details (All fields editable)
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: '#E8E8E8' }}>Report Date *</Label>
                    <Input type="date" value={formData.report_date} onChange={(e) => setFormData({ ...formData, report_date: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                  </div>
                  <div>
                    <Label style={{ color: '#E8E8E8' }}>Agent Name</Label>
                    <Input value={formData.agent_name} onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                  </div>
                </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Duty Start Time</Label>
                <Input type="time" value={formData.duty_start_time} onChange={(e) => setFormData({ ...formData, duty_start_time: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Duty End Time</Label>
                <Input type="time" value={formData.duty_end_time} onChange={(e) => setFormData({ ...formData, duty_end_time: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
            </div>
            {formData.duty_start_time && formData.duty_end_time && (
              <div className="text-sm" style={{ color: '#60A5FA' }}>
                Duty Duration: {calculateDutyDuration(formData.duty_start_time, formData.duty_end_time)}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Actual Departure Time *</Label>
                <Input type="time" value={formData.actual_departure_time} onChange={(e) => setFormData({ ...formData, actual_departure_time: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Actual Passenger Count</Label>
                <Input type="number" value={formData.actual_passenger_count} onChange={(e) => setFormData({ ...formData, actual_passenger_count: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Gate Number</Label>
                <Input value={formData.gate_number} onChange={(e) => setFormData({ ...formData, gate_number: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
            </div>

            {/* Group & Contact Information Section */}
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.5) 0%, rgba(15, 32, 56, 0.5) 100%)',
              border: '1px solid rgba(74, 90, 138, 0.3)',
              borderRadius: '12px',
              padding: '24px',
              marginTop: '8px',
              marginBottom: '8px'
            }}>
              <h3 style={{ 
                color: '#4ADE80', 
                fontSize: '18px', 
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Group & Contact Information
              </h3>
              <p style={{ 
                color: '#B0B0B0', 
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                Essential contact details for tour groups
              </p>

              {/* Group Name */}
              <div style={{ marginBottom: '16px' }}>
                <Label style={{ color: '#E8E8E8' }}>Group Name</Label>
                <Input 
                  value={formData.group_name} 
                  onChange={(e) => setFormData({ ...formData, group_name: e.target.value })} 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>

              {/* Tour Leader Name, Phone, and Emergency Contact in one row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Tour Leader Name</Label>
                  <Input 
                    value={formData.tour_leader_name} 
                    onChange={(e) => setFormData({ ...formData, tour_leader_name: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Tour Leader Phone</Label>
                  <Input 
                    value={formData.tour_leader_phone} 
                    onChange={(e) => setFormData({ ...formData, tour_leader_phone: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Emergency Contact</Label>
                  <Input 
                    value={formData.agency_emergency_contact} 
                    onChange={(e) => setFormData({ ...formData, agency_emergency_contact: e.target.value })} 
                    placeholder="+32 XXX XX XX XX" 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
              </div>
            </div>

            {/* 11. Service Status */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Service Status</Label>
              <Select value={formData.service_completion_status} onValueChange={(value) => setFormData({ ...formData, service_completion_status: value })}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 12. No-Show Passengers (SEARCHABLE SELECT) */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>No-Show Passengers</Label>
              {availablePassengers.length > 0 && (
                <div className="mb-2">
                  <Select onValueChange={(value) => {
                    if (!formData.no_show_list.includes(value)) {
                      setFormData({ ...formData, no_show_list: [...formData.no_show_list, value] });
                    }
                  }}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue placeholder="Select passenger to mark as no-show" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      {availablePassengers.map((passenger, idx) => (
                        <SelectItem key={idx} value={passenger}>
                          {passenger}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.no_show_list.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.no_show_list.map((passenger, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{ background: '#EF4444', color: '#FFFFFF' }}
                    >
                      {passenger}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, no_show_list: formData.no_show_list.filter(p => p !== passenger) })}
                        className="hover:opacity-70"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <textarea 
                className="w-full min-h-[60px] p-2 rounded-md" 
                value={Array.isArray(formData.no_show_list) ? formData.no_show_list.join('\n') : ''} 
                onChange={(e) => {
                  // Split by newline but only filter on blur/submit, not during typing
                  const lines = e.target.value.split('\n');
                  setFormData({ ...formData, no_show_list: lines });
                }}
                onBlur={(e) => {
                  // Clean up empty lines when user finishes editing
                  const cleanedList = formData.no_show_list.filter(p => p && p.trim());
                  setFormData({ ...formData, no_show_list: cleanedList });
                }} 
                placeholder="Or enter manually (one per line)"
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', border: '1px solid' }}
              />
            </div>

            {/* 13. Go-Show List (one per line) */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Go-Show List (one per line)</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={goShowInput} 
                  onChange={(e) => setGoShowInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGoShow())}
                  placeholder="Enter passenger PNR or name" 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
                <Button type="button" onClick={handleAddGoShow} style={{ background: '#4A5A8A', color: '#FFFFFF' }}>
                  Add
                </Button>
              </div>
              {formData.go_show_list.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.go_show_list.map((passenger, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{ background: '#10B981', color: '#FFFFFF' }}
                    >
                      {passenger}
                      <button
                        type="button"
                        onClick={() => handleRemoveGoShow(passenger)}
                        className="hover:opacity-70"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* 14. Connection to Flight/Ship/Bus */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Connection to Flight/Ship/Bus</Label>
              <Input value={formData.connection_info} onChange={(e) => setFormData({ ...formData, connection_info: e.target.value })} placeholder="Enter connection details" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
            </div>

            {/* 15. Operational Notes */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Operational Notes</Label>
              <textarea 
                className="w-full min-h-[100px] p-2 rounded-md" 
                value={formData.operational_notes} 
                onChange={(e) => setFormData({ ...formData, operational_notes: e.target.value })} 
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', border: '1px solid' }}
              />
            </div>
              </>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>Cancel</Button>
              <Button type="submit" disabled={!selectedFlightId} style={{ background: selectedFlightId ? '#4A5A8A' : '#2A3A5A', color: '#FFFFFF', opacity: selectedFlightId ? 1 : 0.5 }}>Create Report</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Report Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Edit Operational Report</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>Update the flight operational details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Flight *</Label>
              <Select value={selectedFlightId} onValueChange={handleFlightSelect} disabled>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="Select a flight" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  {allFlights.map((flight) => (
                    <SelectItem key={flight.id} value={flight.id}>
                      {flight.flight_number} - {formatFlightDate(flight.flight_date)} - {flight.departure_airport} → {flight.arrival_airport}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Report Date *</Label>
                <Input type="date" value={formData.report_date} onChange={(e) => setFormData({ ...formData, report_date: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agent Name</Label>
                <Input value={formData.agent_name} onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Duty Start Time</Label>
                <Input type="time" value={formData.duty_start_time} onChange={(e) => setFormData({ ...formData, duty_start_time: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Duty End Time</Label>
                <Input type="time" value={formData.duty_end_time} onChange={(e) => setFormData({ ...formData, duty_end_time: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
            </div>
            {formData.duty_start_time && formData.duty_end_time && (
              <div className="text-sm" style={{ color: '#60A5FA' }}>
                Duty Duration: {calculateDutyDuration(formData.duty_start_time, formData.duty_end_time)}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Actual Departure Time *</Label>
                <Input type="time" value={formData.actual_departure_time} onChange={(e) => setFormData({ ...formData, actual_departure_time: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Actual Passenger Count</Label>
                <Input type="number" value={formData.actual_passenger_count} onChange={(e) => setFormData({ ...formData, actual_passenger_count: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Gate Number</Label>
                <Input value={formData.gate_number} onChange={(e) => setFormData({ ...formData, gate_number: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
            </div>

            {/* Group & Contact Information Section */}
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.5) 0%, rgba(15, 32, 56, 0.5) 100%)',
              border: '1px solid rgba(74, 90, 138, 0.3)',
              borderRadius: '12px',
              padding: '24px',
              marginTop: '8px',
              marginBottom: '8px'
            }}>
              <h3 style={{ 
                color: '#4ADE80', 
                fontSize: '18px', 
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Group & Contact Information
              </h3>
              <p style={{ 
                color: '#B0B0B0', 
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                Essential contact details for tour groups
              </p>

              {/* Group Name */}
              <div style={{ marginBottom: '16px' }}>
                <Label style={{ color: '#E8E8E8' }}>Group Name</Label>
                <Input 
                  value={formData.group_name} 
                  onChange={(e) => setFormData({ ...formData, group_name: e.target.value })} 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>

              {/* Tour Leader Name, Phone, and Emergency Contact in one row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Tour Leader Name</Label>
                  <Input 
                    value={formData.tour_leader_name} 
                    onChange={(e) => setFormData({ ...formData, tour_leader_name: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Tour Leader Phone</Label>
                  <Input 
                    value={formData.tour_leader_phone} 
                    onChange={(e) => setFormData({ ...formData, tour_leader_phone: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Emergency Contact</Label>
                  <Input 
                    value={formData.agency_emergency_contact} 
                    onChange={(e) => setFormData({ ...formData, agency_emergency_contact: e.target.value })} 
                    placeholder="+32 XXX XX XX XX" 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
              </div>
            </div>

            {/* 11. Service Status */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Service Status</Label>
              <Select value={formData.service_completion_status} onValueChange={(value) => setFormData({ ...formData, service_completion_status: value })}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 12. No-Show Passengers (SEARCHABLE SELECT) */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>No-Show Passengers</Label>
              {availablePassengers.length > 0 && (
                <div className="mb-2">
                  <Select onValueChange={(value) => {
                    if (!formData.no_show_list.includes(value)) {
                      setFormData({ ...formData, no_show_list: [...formData.no_show_list, value] });
                    }
                  }}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue placeholder="Select passenger to mark as no-show" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      {availablePassengers.map((passenger, idx) => (
                        <SelectItem key={idx} value={passenger}>
                          {passenger}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.no_show_list.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.no_show_list.map((passenger, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{ background: '#EF4444', color: '#FFFFFF' }}
                    >
                      {passenger}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, no_show_list: formData.no_show_list.filter(p => p !== passenger) })}
                        className="hover:opacity-70"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <textarea 
                className="w-full min-h-[60px] p-2 rounded-md" 
                value={Array.isArray(formData.no_show_list) ? formData.no_show_list.join('\n') : ''} 
                onChange={(e) => {
                  // Split by newline but only filter on blur/submit, not during typing
                  const lines = e.target.value.split('\n');
                  setFormData({ ...formData, no_show_list: lines });
                }}
                onBlur={(e) => {
                  // Clean up empty lines when user finishes editing
                  const cleanedList = formData.no_show_list.filter(p => p && p.trim());
                  setFormData({ ...formData, no_show_list: cleanedList });
                }} 
                placeholder="Or enter manually (one per line)"
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', border: '1px solid' }}
              />
            </div>

            {/* 13. Go-Show List (one per line) */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Go-Show List (one per line)</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={goShowInput} 
                  onChange={(e) => setGoShowInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGoShow())}
                  placeholder="Enter passenger PNR or name" 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
                <Button type="button" onClick={handleAddGoShow} style={{ background: '#4A5A8A', color: '#FFFFFF' }}>
                  Add
                </Button>
              </div>
              {formData.go_show_list.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.go_show_list.map((passenger, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{ background: '#10B981', color: '#FFFFFF' }}
                    >
                      {passenger}
                      <button
                        type="button"
                        onClick={() => handleRemoveGoShow(passenger)}
                        className="hover:opacity-70"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* 14. Connection to Flight/Ship/Bus */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Connection to Flight/Ship/Bus</Label>
              <Input value={formData.connection_info} onChange={(e) => setFormData({ ...formData, connection_info: e.target.value })} placeholder="Enter connection details" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
            </div>

            {/* 15. Operational Notes */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Operational Notes</Label>
              <textarea 
                className="w-full min-h-[100px] p-2 rounded-md" 
                value={formData.operational_notes} 
                onChange={(e) => setFormData({ ...formData, operational_notes: e.target.value })} 
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', border: '1px solid' }}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>Cancel</Button>
              <Button type="submit" style={{ background: '#4A5A8A', color: '#FFFFFF' }}>Update Report</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Report Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Report Details</DialogTitle>
          </DialogHeader>
          {selectedReport && (() => {
            const flight = allFlights.find(f => f.id === selectedReport.flight_id);
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Flight</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>
                      {flight ? flight.flight_number : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Report Date</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>{formatFlightDate(selectedReport.report_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Agent Name</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.agent_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Status</p>
                    <p className="font-medium capitalize" style={{ color: '#E8E8E8' }}>{selectedReport.service_completion_status}</p>
                  </div>
                </div>
                
                {selectedReport.group_name && (
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Group Name</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.group_name}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  {selectedReport.tour_leader_name && (
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Tour Leader</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.tour_leader_name}</p>
                      {selectedReport.tour_leader_phone && (
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>{selectedReport.tour_leader_phone}</p>
                      )}
                    </div>
                  )}
                  
                  {selectedReport.agency_emergency_contact && (
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Emergency Contact</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.agency_emergency_contact}</p>
                      {selectedReport.emergency_phone && (
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>{selectedReport.emergency_phone}</p>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedReport.connection_info && (
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Connection Information</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.connection_info}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                  {selectedReport.duty_start_time && (
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Duty Start</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>
                        {formatLocalDateTime(selectedReport.duty_start_time)}
                      </p>
                    </div>
                  )}
                  {selectedReport.duty_end_time && (
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Duty End</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>
                        {formatLocalDateTime(selectedReport.duty_end_time)}
                      </p>
                    </div>
                  )}
                  {selectedReport.duty_start_time && selectedReport.duty_end_time && (
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Duration</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>
                        {calculateDutyDuration(selectedReport.duty_start_time, selectedReport.duty_end_time)}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Actual Departure</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>
                      {selectedReport.actual_departure_time ? formatLocalDateTime(selectedReport.actual_departure_time) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Gate Number</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.gate_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Passengers</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.actual_passenger_count || 'N/A'}</p>
                  </div>
                </div>
                
                {(selectedReport.no_show_list?.length > 0 || selectedReport.go_show_list?.length > 0) && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedReport.no_show_list?.length > 0 && (
                      <div>
                        <p className="text-sm mb-2" style={{ color: '#B0B0B0' }}>No-Shows</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedReport.no_show_list.map((passenger, idx) => (
                            <span key={idx} className="px-3 py-1 rounded-full text-sm" style={{ background: '#EF4444', color: '#FFFFFF' }}>
                              {passenger}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedReport.go_show_list?.length > 0 && (
                      <div>
                        <p className="text-sm mb-2" style={{ color: '#B0B0B0' }}>Go-Shows</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedReport.go_show_list.map((passenger, idx) => (
                            <span key={idx} className="px-3 py-1 rounded-full text-sm" style={{ background: '#10B981', color: '#FFFFFF' }}>
                              {passenger}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedReport.operational_notes && (
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Notes</p>
                    <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedReport.operational_notes}</p>
                  </div>
                )}
                
                {/* Email Send History */}
                {selectedReport.email_history && selectedReport.email_history.length > 0 && (
                  <div className="mt-4 pt-4" style={{ borderTop: '1px solid #2D3748' }}>
                    <p className="text-sm font-semibold mb-3" style={{ color: '#FFD700' }}>📧 Email Send History</p>
                    <div className="space-y-2">
                      {selectedReport.email_history.map((history, idx) => (
                        <div key={idx} className="p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span style={{ color: '#B0B0B0' }}>Sent to: </span>
                              <span style={{ color: '#E8E8E8' }}>{history.sent_to}</span>
                            </div>
                            {history.cc && (
                              <div>
                                <span style={{ color: '#B0B0B0' }}>CC: </span>
                                <span style={{ color: '#E8E8E8' }}>{history.cc}</span>
                              </div>
                            )}
                            <div>
                              <span style={{ color: '#B0B0B0' }}>Sent by: </span>
                              <span style={{ color: '#E8E8E8' }}>{history.sent_by_name || history.sent_by}</span>
                            </div>
                            <div>
                              <span style={{ color: '#B0B0B0' }}>Date: </span>
                              <span style={{ color: '#E8E8E8' }}>
                                {new Date(history.sent_at).toLocaleString('en-GB', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric', 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="mt-1">
                            <span className="px-2 py-1 rounded text-xs" style={{ background: '#10B981', color: '#FFFFFF' }}>
                              ✓ Sent Successfully
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
